import React, { FC, ReactNode, useCallback } from "react";

import { useIntl } from "react-intl";

import User from "@mapmycustomers/shared/types/User";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { Alert } from "@mapmycustomers/ui";

import ButtonLink from "@app/component/ButtonLink";
import localSettings from "@app/config/LocalSettings";
import { isMember, isReadOnly } from "@app/store/iam/util";
import Scope from "@app/types/dashboard/Scope";

import styles from "./TeamMembersAlert.module.scss";

export interface TeamMembersAlertProps {
  currentUser: User;
  scope: Scope;
}

const TeamMembersAlert: FC<TeamMembersAlertProps> = ({ currentUser, scope }) => {
  const intl = useIntl();

  const [hidden, hide] = useBoolean(false);

  const handleDontShowAgainClick = useCallback(() => {
    localSettings.setTeamMemberBoardAlertDismissed(true);
    hide();
  }, [hide]);

  if (
    !isMember(currentUser) ||
    !isReadOnly(currentUser) ||
    !scope.userId ||
    !scope.teamId ||
    hidden ||
    localSettings.isTeamMemberBoardAlertDismissed()
  ) {
    return null;
  }

  return (
    <Alert
      action={
        <ButtonLink onClick={handleDontShowAgainClick}>
          {intl.formatMessage({
            id: "dashboard.grid.alert.member.dontShowAgain",
            defaultMessage: "Don't show again",
            description: "Don't show again button for an alert shown to team members",
          })}
        </ButtonLink>
      }
      className={styles.container}
      closable
      message={intl.formatMessage(
        {
          id: "dashboard.grid.alert.member",
          defaultMessage:
            "<p>Did you know that your manager can update this view's charts and arrangement?</p><p>Reach out to them to customize this view on your behalf.</p>",
          description: "An alert shown to team members",
        },
        { p: (text: ReactNode[]) => <p>{text}</p> }
      )}
      onClose={hide}
      showIcon
      type="info"
    />
  );
};

export default TeamMembersAlert;
